<template>
  <div>
    <v-btn
      depressed
      color="primary"
      min-width="36"
      class="pa-0 rounded-lg font-weight-normal"
      style="text-transform: unset"
      @click="showDialog = true"
    >
      <v-icon small>mdi-plus</v-icon>
    </v-btn>

    <v-dialog v-model="showDialog" scrollable hide-overlay max-width="800px">
      <v-card class="rounded-lg textLight--text" style="height: fit-content">
        <v-container fluid>
          <v-row class="pb-2 pa-5">
            <v-col cols="auto">
              <div
                class="font-weight-medium text-h5 text-start textLight--text"
              >
                {{
                  $t("organization.organizationProfile.assignCourseValidity")
                }}
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col order="2" order-md="1">
              <ValidationObserver ref="observer">
                <v-row class="pa-5">
                  <v-col>
                    <!--course-->
                    <v-row>
                      <v-col cols="3" class="d-flex align-center">
                        <p
                          class="ma-0 text-start font-weight-thin text-truncate"
                        >
                          {{ $t("course.course") }}
                        </p>
                      </v-col>

                      <v-col cols="9">
                        <validation-provider
                          v-slot="{ errors }"
                          rules="required"
                        >
                          <v-autocomplete
                            hide-details
                            :error-messages="errors"
                            v-model="selectedTraining"
                            outlined
                            dense
                            :items="trainings"
                            item-value="_id"
                            return-object
                            flat
                            solo
                            :label="$t('user.registration.selectCourse')"
                            class="rounded-lg"
                            :filter="customLocalisedAutoCompleteFilter"
                          >
                            <template v-slot:selection="{ item }">
                              {{ getLocalisedValue(item.title) }}
                            </template>

                            <template v-slot:item="{ item }">
                              <div
                                class="d-flex flex-column text-start"
                                style="width: 100%"
                              >
                                {{ getLocalisedValue(item.title) }}
                                <v-divider></v-divider>
                              </div>
                            </template>
                          </v-autocomplete>
                        </validation-provider>
                      </v-col>
                    </v-row>

                    <!--default validity-->
                    <v-row>
                      <v-col cols="3" class="d-flex align-center">
                        <p class="ma-0 text-start font-weight-thin">
                          {{
                            $t(
                              "organization.organizationProfile.existingValidity"
                            )
                          }}
                        </p>
                      </v-col>

                      <v-col cols="9">
                        <validation-provider v-slot="{ errors }">
                          <v-text-field
                            hide-details
                            readonly
                            type="number"
                            :value="defaultCertificateValidity"
                            :error-messages="errors"
                            class="rounded-lg text-field-prefix-opacity"
                            outlined
                            dense
                          >
                          </v-text-field>
                        </validation-provider>
                      </v-col>
                    </v-row>

                    <!--new validity-->
                    <v-row>
                      <v-col cols="3" class="d-flex align-center">
                        <p class="ma-0 text-start font-weight-thin">
                          {{
                            $t("organization.organizationProfile.newValidity")
                          }}
                        </p>
                      </v-col>

                      <v-col cols="9">
                        <validation-provider
                          v-slot="{ errors }"
                          rules="required"
                        >
                          <v-text-field
                            hide-details
                            type="number"
                            min="0"
                            v-model.number="certificateValidity"
                            :error-messages="errors"
                            class="rounded-lg text-field-prefix-opacity"
                            outlined
                            dense
                          >
                          </v-text-field>
                        </validation-provider>
                      </v-col>
                    </v-row>

                    <!--add button-->
                    <v-row>
                      <v-col cols="12" class="text-end">
                        <v-btn
                          depressed
                          min-width="100"
                          :disabled="
                            isSaving ||
                            !selectedTraining ||
                            certificateValidity == null
                          "
                          color="primary"
                          style="text-transform: unset"
                          class="rounded-lg font-weight-normal"
                          @click="addTrainingValidity"
                        >
                          {{ $t("organization.organizationProfile.add") }}
                        </v-btn>
                      </v-col>
                    </v-row>

                    <!--table-->
                    <v-row>
                      <v-col>
                        <v-data-table
                          disable-pagination
                          disable-sort
                          disable-filtering
                          hide-default-footer
                          :headers="headers"
                          :server-items-length="selectedItems.length"
                          :items="selectedItems"
                          :mobile-breakpoint="0"
                        >
                          <template #item.training="{ item }">
                            {{
                              item.training
                                ? getLocalisedValue(item.training.title)
                                : "-"
                            }}
                          </template>

                          <template #item.existingValidity="{ item }">
                            {{
                              item.training
                                ? item.training.certificateValidity
                                : "-"
                            }}
                          </template>

                          <template #item.newValidity="{ item }">
                            {{ item.certificateValidity || "-" }}
                          </template>

                          <template #item.actions="{ index }">
                            <div class="d-flex justify-space-between">
                              <v-icon
                                size="16"
                                color="error"
                                @click="selectedItems.splice(index, 1)"
                              >
                                mdi-trash-can-outline
                              </v-icon>
                            </div>
                          </template>

                          <template v-slot:no-data>
                            <span
                              class="subtitle-2 text-center font-weight-regular"
                            >
                              {{ $t("noItems") }}
                            </span>
                          </template>
                        </v-data-table>
                      </v-col>
                    </v-row>

                    <!--save button-->
                    <v-row>
                      <v-col cols="12">
                        <v-btn
                          depressed
                          block
                          :disabled="!selectedItems.length"
                          color="primary"
                          style="text-transform: unset"
                          class="rounded-lg font-weight-normal"
                          :loading="isSaving"
                          @click="createOrUpdateTrainingValidities"
                        >
                          {{ $t("organization.organizationProfile.change") }}
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </ValidationObserver>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import "../../../core-module/util/validationRules";
import { createOrUpdateTrainingValidities } from "@/app/organization-module/methods/createOrUpdateOrDeleteTrainingValidiy";

export default {
  name: "CreateOrUpdateTrainingValiditiesDialog",
  props: {
    organization: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showDialog: false,
      trainings: [],

      selectedTraining: null,
      certificateValidity: null,
      selectedItems: [],
      isSaving: false,
    };
  },
  computed: {
    defaultCertificateValidity() {
      return this.selectedTraining?.certificateValidity ?? "";
    },
    headers() {
      return [
        {
          text: this.$t("organization.organizationProfile.courseName"),
          value: "training",
          align: "start",
        },
        {
          text: this.$t("organization.organizationProfile.existingValidity"),
          value: "existingValidity",
          width: "200px",
          align: "center",
        },
        {
          text: this.$t("organization.organizationProfile.newValidity"),
          value: "newValidity",
          width: "200px",
          align: "center",
        },
        {
          text: "",
          value: "actions",
          width: "48px",
          align: "end",
        },
      ];
    },
  },
  watch: {
    showDialog() {
      if (!this.showDialog) {
        this.selectedTraining = null;
        this.certificateValidity = null;
        this.selectedItems = [];
        this.$refs.observer.reset();
      }
    },
  },
  apollo: {
    getTrainings() {
      return {
        query: require("../../../course-module/api/query/getTrainings.gql"),
        variables: {
          limit: 0,
          skip: 0,
          filterInput: { approvalStatus: "APPROVED" },
        },
        result({ data }) {
          if (data?.getTrainings?.trainings) {
            this.trainings =
              data.getTrainings.trainings?.filter(x => x.isActive === true) ??
              [];
          }
        },
      };
    },
  },
  methods: {
    async addTrainingValidity() {
      const isValid = await this.$refs.observer.validate();
      if (!isValid) return;

      const item = {
        training: this.selectedTraining,
        certificateValidity: this.certificateValidity,
      };

      const existingIndex = this.selectedItems.findIndex(
        x => x.training._id === this.selectedTraining._id
      );

      if (existingIndex >= 0) {
        this.$set(this.selectedItems, existingIndex, item);
      } else {
        this.selectedItems.push(item);
      }
    },
    createOrUpdateTrainingValidities() {
      if (!this.selectedItems.length) return;
      this.isSaving = true;

      const inputs = this.selectedItems.map(x => ({
        organization: this.organization._id,
        training: x.training._id,
        certificateValidity: x.certificateValidity,
      }));

      createOrUpdateTrainingValidities(inputs).then(res => {
        if (res.error) this.$toast.error("Some error occurred!");
        else this.showDialog = false;
        this.isSaving = false;
      });
    },
  },
};
</script>
